
import { useState, useRef } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import type { ReportTemplate, ReportElement } from '@/../../shared/schema';

interface ElementPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ReportDesignerProps {
  template?: ReportTemplate;
  onSave: (template: Partial<ReportTemplate>, elements: ReportElement[]) => void;
}

export function ReportDesigner({ template, onSave }: ReportDesignerProps) {
  const [name, setName] = useState(template?.name || '');
  const [category, setCategory] = useState(template?.category || 'custom');
  const [elements, setElements] = useState<Partial<ReportElement>[]>([]);
  const [selectedElement, setSelectedElement] = useState<number | null>(null);
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const elementTypes = [
    { type: 'textbox', label: 'Text Box', icon: '📝' },
    { type: 'label', label: 'Label', icon: '🏷️' },
    { type: 'table', label: 'Table', icon: '📊' },
    { type: 'chart', label: 'Chart', icon: '📈' },
    { type: 'image', label: 'Image', icon: '🖼️' },
    { type: 'barcode', label: 'Barcode', icon: '📷' },
  ];

  const handleDragStart = (type: string) => {
    setDraggedElement(type);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedElement || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newElement: Partial<ReportElement> = {
      type: draggedElement as any,
      name: `${draggedElement}_${elements.length + 1}`,
      properties: {},
      position: { x: Math.round(x), y: Math.round(y), width: 150, height: 50 },
      sort_order: elements.length,
    };

    setElements([...elements, newElement]);
    setDraggedElement(null);
  };

  const handleElementClick = (index: number) => {
    setSelectedElement(index);
  };

  const updateElement = (index: number, updates: Partial<ReportElement>) => {
    const updated = [...elements];
    updated[index] = { ...updated[index], ...updates };
    setElements(updated);
  };

  const deleteElement = (index: number) => {
    setElements(elements.filter((_, i) => i !== index));
    if (selectedElement === index) setSelectedElement(null);
  };

  const handleSave = () => {
    onSave(
      {
        name,
        category,
        layout: { orientation: 'portrait', pageSize: 'A4' },
        data_sources: [],
      },
      elements as ReportElement[]
    );
  };

  return (
    <div className="grid grid-cols-12 gap-4 h-screen">
      {/* Toolbox */}
      <div className="col-span-2 bg-white p-4 border-r overflow-y-auto">
        <h3 className="font-semibold mb-4">Elements</h3>
        <div className="space-y-2">
          {elementTypes.map((el) => (
            <div
              key={el.type}
              draggable
              onDragStart={() => handleDragStart(el.type)}
              className="p-3 border rounded cursor-move hover:bg-gray-50 flex items-center gap-2"
            >
              <span>{el.icon}</span>
              <span className="text-sm">{el.label}</span>
            </div>
          ))}
        </div>

        <h3 className="font-semibold mt-6 mb-4">Data Sources</h3>
        <Button variant="secondary" className="w-full text-sm">
          + Add Data Source
        </Button>
      </div>

      {/* Canvas */}
      <div className="col-span-7 bg-gray-100 p-4 overflow-auto">
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Report Name"
                className="text-xl font-semibold border-none outline-none"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-1 border rounded"
            >
              <option value="sales">Sales</option>
              <option value="inventory">Inventory</option>
              <option value="customer">Customer</option>
              <option value="financial">Financial</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div
            ref={canvasRef}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="relative bg-white border-2 border-dashed border-gray-300 rounded"
            style={{ width: '210mm', height: '297mm', minHeight: '400px' }}
          >
            {elements.map((element, index) => (
              <div
                key={index}
                onClick={() => handleElementClick(index)}
                className={`absolute border-2 ${
                  selectedElement === index
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-400 bg-white'
                } cursor-move p-2 rounded`}
                style={{
                  left: (element.position as ElementPosition)?.x,
                  top: (element.position as ElementPosition)?.y,
                  width: (element.position as ElementPosition)?.width,
                  height: (element.position as ElementPosition)?.height,
                }}
              >
                <div className="text-xs text-gray-600">{element.type}</div>
                <div className="text-sm truncate">{element.name}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Properties Panel */}
      <div className="col-span-3 bg-white p-4 border-l overflow-y-auto">
        <h3 className="font-semibold mb-4">Properties</h3>
        {selectedElement !== null && elements[selectedElement] ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={elements[selectedElement].name}
                onChange={(e) =>
                  updateElement(selectedElement, { name: e.target.value })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <input
                type="text"
                value={elements[selectedElement].type}
                disabled
                className="w-full px-3 py-2 border rounded bg-gray-50"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">X</label>
                <input
                  type="number"
                  value={(elements[selectedElement].position as ElementPosition)?.x || 0}
                  onChange={(e) =>
                    updateElement(selectedElement, {
                      position: {
                        ...(elements[selectedElement].position as ElementPosition),
                        x: parseInt(e.target.value),
                      },
                    })
                  }
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Y</label>
                <input
                  type="number"
                  value={(elements[selectedElement].position as ElementPosition)?.y || 0}
                  onChange={(e) =>
                    updateElement(selectedElement, {
                      position: {
                        ...(elements[selectedElement].position as ElementPosition),
                        y: parseInt(e.target.value),
                      },
                    })
                  }
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">Width</label>
                <input
                  type="number"
                  value={(elements[selectedElement].position as ElementPosition)?.width || 0}
                  onChange={(e) =>
                    updateElement(selectedElement, {
                      position: {
                        ...(elements[selectedElement].position as ElementPosition),
                        width: parseInt(e.target.value),
                      },
                    })
                  }
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Height</label>
                <input
                  type="number"
                  value={(elements[selectedElement].position as ElementPosition)?.height || 0}
                  onChange={(e) =>
                    updateElement(selectedElement, {
                      position: {
                        ...(elements[selectedElement].position as ElementPosition),
                        height: parseInt(e.target.value),
                      },
                    })
                  }
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Data Binding
              </label>
              <input
                type="text"
                value={elements[selectedElement].data_binding || ''}
                onChange={(e) =>
                  updateElement(selectedElement, {
                    data_binding: e.target.value,
                  })
                }
                placeholder="${dataSource.field}"
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            <Button
              variant="danger"
              onClick={() => deleteElement(selectedElement)}
              className="w-full"
            >
              Delete Element
            </Button>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">
            Select an element to edit its properties
          </p>
        )}

        <div className="mt-6 pt-6 border-t">
          <Button onClick={handleSave} className="w-full">
            Save Template
          </Button>
        </div>
      </div>
    </div>
  );
}
